"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { normalizeLocaleLanguage } from "@/constants/localization";
import { useAlert } from "@/hooks/useAlert";

export const LANGUAGE_MISMATCH_NOTICE_STORAGE_KEY = "gail_lang_mismatch_notice_shown";

/**
 * One-time notice when the browser locale differs from the active app language.
 * Directs users to set their preferred language in profile instead of using
 * browser translation (which breaks React DOM reconciliation).
 */
export default function LanguageMismatchNotice() {
  const { i18n, t } = useTranslation("common");
  const { setAlert } = useAlert();
  const router = useRouter();
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (hasShownRef.current) {
      return;
    }

    try {
      if (globalThis.localStorage.getItem(LANGUAGE_MISMATCH_NOTICE_STORAGE_KEY) === "1") {
        return;
      }
    } catch {
      return;
    }

    const browserLanguage = normalizeLocaleLanguage(globalThis.navigator?.language);
    const appLanguage = normalizeLocaleLanguage(i18n.language);

    if (browserLanguage === appLanguage) {
      return;
    }

    hasShownRef.current = true;

    try {
      globalThis.localStorage.setItem(LANGUAGE_MISMATCH_NOTICE_STORAGE_KEY, "1");
    } catch {
      // Still show the notice even if localStorage is unavailable.
    }

    setAlert({
      status: "info",
      message: t("languageMismatchNotice.message"),
      secondsToClose: 30,
      btnLabel: t("languageMismatchNotice.goToProfile"),
      btnAction: () => {
        router.push("/profile");
      },
    });
  }, [i18n.language, router, setAlert, t]);

  return null;
}
